import { useState } from "react";
import { CalendarClock, CreditCard, Info } from "lucide-react";

const money = (n) => `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });

const afterpaySchedule = (total) => {
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / 4);
  const first = cents - base * 3;
  return [first, base, base, base].map((c, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i * 14);
    return { amount: c / 100, label: i === 0 ? "Today" : fmtDate(d) };
  });
};

const AfterpayPlan = ({ total }) => (
  <div data-testid="afterpay-plan">
    <div className="grid grid-cols-4 gap-2">
      {afterpaySchedule(total).map((p, i) => (
        <div key={i} className={`rounded-xl p-3 text-center border ${i === 0 ? "bg-[#b2fce4]/15 border-[#b2fce4]/50" : "bg-white/[0.03] border-white/10"}`} data-testid={`afterpay-payment-${i}`}>
          <p className="text-white font-mono font-semibold text-sm sm:text-base">{money(p.amount)}</p>
          <p className={`text-[11px] mt-1 ${i === 0 ? "text-[#b2fce4] font-semibold" : "text-white/45"}`}>{p.label}</p>
        </div>
      ))}
    </div>
    <p className="text-white/50 text-xs mt-3">4 interest-free payments, every 2 weeks. Late fees may apply. Afterpay approves each order individually, so larger orders may need a bigger first payment.</p>
  </div>
);

const ZipPlan = ({ total }) => {
  const months = Math.max(1, Math.ceil(total / 40));
  return (
    <div data-testid="zip-plan">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl p-3 text-center bg-[#aa8fff]/15 border border-[#aa8fff]/50">
          <p className="text-white font-mono font-semibold">$0.00</p>
          <p className="text-[11px] mt-1 text-[#c9b8ff] font-semibold">Today</p>
        </div>
        <div className="rounded-xl p-3 text-center bg-white/[0.03] border border-white/10">
          <p className="text-white font-mono font-semibold">from $40</p>
          <p className="text-[11px] mt-1 text-white/45">per month</p>
        </div>
        <div className="rounded-xl p-3 text-center bg-white/[0.03] border border-white/10">
          <p className="text-white font-mono font-semibold" data-testid="zip-months">≤ {months} mo</p>
          <p className="text-[11px] mt-1 text-white/45">at the minimum</p>
        </div>
      </div>
      <p className="text-white/50 text-xs mt-3">
        Zip is a flexible interest-free account. Pay nothing today, then repay weekly, fortnightly or monthly (minimum $40/month). Pay your balance in full each month to avoid the $9.95 monthly account fee. Zip Pay limits are up to $1,000. Larger amounts may use Zip Money.
      </p>
    </div>
  );
};

export const PayLaterCalculator = ({ total }) => {
  const [tab, setTab] = useState("afterpay");
  if (!total || total <= 0) return null;
  const tabCls = (on) => `flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${on ? "bg-white text-black" : "text-white/60 hover:text-white"}`;

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#b2fce4]/[0.07] via-dream-surface to-[#aa8fff]/[0.07] p-5 mb-6" data-testid="pay-later-calculator">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-white font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#b2fce4]" /> Pay later calculator</p>
        <p className="text-white/50 text-xs flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> on {money(total)}</p>
      </div>
      <div className="flex gap-1 p-1 rounded-full bg-black/40 border border-white/10 mb-4">
        <button type="button" onClick={() => setTab("afterpay")} className={tabCls(tab === "afterpay")} data-testid="paylater-tab-afterpay">Afterpay</button>
        <button type="button" onClick={() => setTab("zip")} className={tabCls(tab === "zip")} data-testid="paylater-tab-zip">Zip Pay</button>
      </div>
      {tab === "afterpay" ? <AfterpayPlan total={total} /> : <ZipPlan total={total} />}
      <p className="text-white/60 text-xs mt-4 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Choose Afterpay or Zip as your payment method on the secure checkout page. Estimates only; final terms are set by the provider.</p>
    </div>
  );
};

export const AfterpayToday = ({ price, className = "", testId }) => {
  const value = typeof price === "number" ? price : parseFloat(String(price).replace(/[^0-9.]/g, ""));
  if (!value || /\/\s*h/i.test(String(price))) return null;
  const today = Math.round(value * 100 - Math.floor((value * 100) / 4) * 3) / 100;
  return (
    <p className={`text-[#b2fce4] text-xs ${className}`} data-testid={testId}>
      or pay <span className="font-semibold">{money(today)}</span> today with Afterpay
    </p>
  );
};
